/**
 * WB4 — Rule-based operational palette query (NL patterns → filter commands).
 *
 * Not live LLM — honest rule-pilot matching for operational phrases.
 */

import type { ResolvedCommand } from './command-resolver';
import type { WorkbenchCommandContext } from './command-resolver';
import {
  PALETTE_OPERATIONAL_FILTER_SPECS,
  type PaletteOperationalFilterSpec,
} from './workbench-palette-operational-filter';

export interface OperationalPaletteQueryMatch {
  spec: PaletteOperationalFilterSpec;
  score: number;
  matchedTerms: readonly string[];
}

function tokenizeQuery(query: string): string[] {
  return query
    .trim()
    .toLowerCase()
    .split(/[\s,·]+/)
    .filter(Boolean);
}

function scoreSpecAgainstTokens(
  spec: PaletteOperationalFilterSpec,
  tokens: readonly string[]
): OperationalPaletteQueryMatch | undefined {
  if (tokens.length === 0) {
    return undefined;
  }

  const matched = spec.keywords.filter((keyword) =>
    tokens.some((token) => token.includes(keyword) || keyword.includes(token))
  );

  if (matched.length === 0) {
    return undefined;
  }

  return {
    spec,
    score: matched.length,
    matchedTerms: matched,
  };
}

export function resolveOperationalPaletteQueryMatches(
  query: string,
  ctx: WorkbenchCommandContext
): OperationalPaletteQueryMatch[] {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) {
    return [];
  }

  const hasCustomerCue = tokens.some((token) =>
    ['고객', 'customer', '거래처', '이'].includes(token)
  );
  const hasSupplierCue = tokens.some((token) =>
    ['공급', 'supplier', '조달', '발주'].includes(token)
  );
  const customerDomain =
    ctx.object &&
    (ctx.businessDomain === 'customer' ||
      ctx.businessDomain === 'counterparty' ||
      ctx.businessDomain === 'external_entity');
  const supplierDomain =
    ctx.object &&
    (ctx.businessDomain === 'supplier' ||
      ctx.businessDomain === 'entity_supplier' ||
      ctx.businessDomain === 'external_entity');

  const matches: OperationalPaletteQueryMatch[] = [];

  for (const spec of PALETTE_OPERATIONAL_FILTER_SPECS) {
    if (spec.handlerRef === 'filter:customer-overdue-orders') {
      if (!hasCustomerCue && !customerDomain) {
        continue;
      }
    }
    if (
      spec.handlerRef === 'filter:supplier-inventory-shortages' ||
      spec.handlerRef === 'filter:supplier-receiving-queue'
    ) {
      if (!hasSupplierCue && !supplierDomain) {
        continue;
      }
    }

    const scored = scoreSpecAgainstTokens(spec, tokens);
    if (scored) {
      matches.push(scored);
    }
  }

  return matches.sort((a, b) => b.score - a.score);
}

export function buildOperationalFilterResolvedCommand(
  spec: PaletteOperationalFilterSpec,
  _ctx: WorkbenchCommandContext
): ResolvedCommand {
  return {
    id: spec.id,
    label: spec.label,
    section: 'action',
    kind: 'action',
    group: 'filter',
    handlerRef: spec.handlerRef,
    priority: 100,
  };
}

export function resolveOperationalPaletteCommandsFromQuery(
  ctx: WorkbenchCommandContext,
  query: string
): ResolvedCommand[] {
  return resolveOperationalPaletteQueryMatches(query, ctx).map((match) =>
    buildOperationalFilterResolvedCommand(match.spec, ctx)
  );
}
